/**
 * Reactive `prefers-reduced-motion` subscription hook.
 *
 * Reads the user's OS-level "reduce motion" accessibility preference via
 * `window.matchMedia` and keeps the value in sync with live changes. Any
 * component that drives CSS transitions or SVG animations can call it to
 * collapse durations to zero when the user requested reduced motion.
 *
 * Lives in `src/hooks/` because it is not tied to any component; every
 * consumer across the codebase can reuse it.
 */

import { useEffect, useState } from "react";

/** Media query used to detect the reduced-motion preference. */
const REDUCED_MOTION_MEDIA_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * React hook that reports the current `prefers-reduced-motion` state.
 * Subscribes to media-query changes on mount and unsubscribes on unmount.
 *
 * @returns `true` when the user prefers reduced motion, `false` otherwise.
 */
export const useReducedMotion = (): boolean => {
    const [ reducedMotion, setReducedMotion ] = useState<boolean>( () => {
        if ( typeof window === "undefined" ) {
            return false;
        }

        return window.matchMedia( REDUCED_MOTION_MEDIA_QUERY ).matches;
    } );

    useEffect( () => {
        if ( typeof window === "undefined" ) {
            return;
        }

        const mediaQueryList = window.matchMedia( REDUCED_MOTION_MEDIA_QUERY );

        /**
         * Synchronises the React state with live changes to the media
         * query (for example when the user flips the accessibility
         * preference while the app is open).
         *
         * @param event Media-query change event.
         */
        const handleChange = ( event: MediaQueryListEvent ): void => {
            setReducedMotion( event.matches );
        };

        mediaQueryList.addEventListener( "change", handleChange );

        return () => {
            mediaQueryList.removeEventListener( "change", handleChange );
        };
    }, [] );

    return reducedMotion;
};
